import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import './styles/globals.css';
import { AuthProvider } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import AdminOnlyRoute from '@/components/auth/AdminOnlyRoute';
import OwnerOnlyRoute from '@/components/auth/OwnerOnlyRoute';
import ParentOnlyRoute from '@/components/auth/ParentOnlyRoute';
import LoginPage from '@/pages/LoginPage';
import ParentLoginPage from '@/pages/ParentLoginPage';
import ParentActivatePage from '@/pages/ParentActivatePage';
import Dashboard from '@/pages/Dashboard';
import PublicBookingPage from '@/pages/PublicBookingPage';
import PublicBookingCheckoutPage from '@/pages/PublicBookingCheckoutPage';
import PublicBookingCompletedPage from '@/pages/PublicBookingCompletedPage';
import PublicCartPage from '@/pages/PublicCartPage';
import PublicClubEventsPage from '@/pages/PublicClubEventsPage';
import PublicClubsBrowsePage from '@/pages/PublicClubsBrowsePage';
import ActivitiesPage from '@/pages/ActivitiesPage';
import ActivityCreatePage from '@/pages/ActivityCreatePage';
import ActivityOverviewPage from '@/pages/ActivityOverviewPage';
import ActivityWalkInPage from '@/pages/ActivityWalkInPage';
import AttendeesPage from '@/pages/AttendeesPage';
import AttendeeOverviewPage from '@/pages/AttendeeOverviewPage';
import AttendeeCreatePage from '@/pages/AttendeeCreatePage';
import BookingsPage from '@/pages/BookingsPage';
import BookingOverviewPage from '@/pages/BookingOverviewPage';
import BillingPage from '@/pages/BillingPage';
import ClubProfilePage from '@/pages/ClubProfilePage';
import UsersPage from '@/pages/UsersPage';
import UsersCreatePage from '@/pages/UsersCreatePage';
import UsersEditPage from '@/pages/UsersEditPage';
import DiscountsPage from '@/pages/DiscountsPage';
import DiscountCreatePage from '@/pages/DiscountCreatePage';
import ParentBookingsPage from '@/pages/ParentBookingsPage';
import ParentChildrenPage from '@/pages/ParentChildrenPage';
import ParentProfilePage from '@/pages/ParentProfilePage';
import SubdomainAdminEntryPage from '@/pages/SubdomainAdminEntryPage';
import AdminSubscriptionRequiredPage from '@/pages/AdminSubscriptionRequiredPage';
import { StripeConnectStatusProvider } from '@/contexts/StripeConnectStatusContext';
import { auth } from '@/store/authStore';
import { getClubSiteSlugFromHostname } from '@/utils/publicSite';
import { ClaritySessionSync } from '@/components/analytics/ClaritySessionSync';

function App() {
  const clubSiteSlug = getClubSiteSlugFromHostname();
  const homeTarget = clubSiteSlug
    ? null
    : (auth.isAuthenticated() ? (auth.getUser().role === 'parent' ? '/my-bookings' : '/dashboard') : '/login');

  return (
    <Router>
      <AuthProvider>
        <ClaritySessionSync />
        <StripeConnectStatusProvider>
          <Routes>
            <Route path="/" element={clubSiteSlug ? <PublicClubEventsPage clubSlugOverride={clubSiteSlug} /> : <Navigate to={homeTarget || '/login'} replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin" element={<SubdomainAdminEntryPage />} />
            <Route
              path="/billing-required"
              element={
                <ProtectedRoute>
                  <AdminSubscriptionRequiredPage />
                </ProtectedRoute>
              }
            />
            <Route path="/parent/login" element={<ParentLoginPage />} />
            <Route path="/parent/activate" element={<ParentActivatePage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <Dashboard />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/activities"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <ActivitiesPage />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/activities/new"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <ActivityCreatePage />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/activities/:eventId/edit"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <ActivityCreatePage />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/activities/:eventId"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <ActivityOverviewPage />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/activities/:eventId/walk-in"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <ActivityWalkInPage />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/attendees"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <AttendeesPage />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/attendees/new"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <AttendeeCreatePage />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/attendees/:attendeeId"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <AttendeeOverviewPage />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <BookingsPage />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings/new"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <AttendeeCreatePage />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings/:bookingId"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <BookingOverviewPage />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <OwnerOnlyRoute>
                      <UsersPage />
                    </OwnerOnlyRoute>
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/new"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <OwnerOnlyRoute>
                      <UsersCreatePage />
                    </OwnerOnlyRoute>
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:userId/edit"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <OwnerOnlyRoute>
                      <UsersEditPage />
                    </OwnerOnlyRoute>
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/club-profile"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <ClubProfilePage />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <OwnerOnlyRoute>
                      <BillingPage />
                    </OwnerOnlyRoute>
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/discounts"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <DiscountsPage />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/discounts/new"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <DiscountCreatePage />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/discounts/:discountId/edit"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <DiscountCreatePage />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-bookings"
              element={
                <ProtectedRoute>
                  <ParentOnlyRoute>
                    <ParentBookingsPage />
                  </ParentOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-children"
              element={
                <ProtectedRoute>
                  <ParentOnlyRoute>
                    <ParentChildrenPage />
                  </ParentOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-account"
              element={
                <ProtectedRoute>
                  <ParentOnlyRoute>
                    <ParentProfilePage />
                  </ParentOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route path="/clubs" element={clubSiteSlug ? <PublicClubEventsPage clubSlugOverride={clubSiteSlug} /> : <PublicClubsBrowsePage />} />
            <Route path="/cart" element={<PublicCartPage />} />
            <Route path="/book/:eventSlug" element={<PublicBookingPage />} />
            <Route path="/book/:eventSlug/checkout" element={<PublicBookingCheckoutPage />} />
            <Route path="/book/:eventSlug/completed" element={<PublicBookingCompletedPage />} />
            <Route path="/clubs/:clubSlug" element={<PublicClubEventsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </StripeConnectStatusProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
